import awbService, { CreateAWBRequest } from '../services/awbService';

interface TestResult {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
}

interface AWBTestSuite {
    testCreateAWB: () => Promise<TestResult>;
    testGetAWB: (awbNumber: string) => Promise<TestResult>;
    testUpdateAWB: (awbNumber: string) => Promise<TestResult>;
    testSearchAWBs: () => Promise<TestResult>;
    testAddTrackingEvent: (awbNumber: string) => Promise<TestResult>;
    testUpdateStatus: (awbNumber: string) => Promise<TestResult>;
    testAddComment: (awbNumber: string) => Promise<TestResult>;
    testGetAWBStats: () => Promise<TestResult>;
    testDeleteAWB: (awbNumber: string) => Promise<TestResult>;
    runFullTestSuite: () => Promise<void>;
    seedTestData: () => Promise<void>;
    clearTestData: () => Promise<void>;
}

// Sample test data for AWB creation
const getSampleAWBData = (): CreateAWBRequest => ({
    // Basic Information
    customer: '67890abcdef1234567890123', // Will need to be replaced with actual customer ID
    company: '67890abcdef1234567890456', // Will need to be replaced with actual company ID
    destination: '67890abcdef1234567890789', // Will need to be replaced with actual country ID
    product: '67890abcdef1234567890101', // Will need to be replaced with actual product ID
    bookingDate: new Date(),
    bookingTime: new Date().toTimeString().slice(0, 5),
    service: '67890abcdef1234567890102', // Will need to be replaced with actual service ID
    shipmentValue: 1000,
    currency: '67890abcdef1234567890103', // Will need to be replaced with actual currency ID
    paymentMethod: 'PREPAID',

    // Shipper Information
    shipper: {
        personName: 'John Doe',
        address1: '123 Main Street',
        city: 'Mumbai',
        stateCounty: 'Maharashtra',
        country: 'India',
        phoneNumber: '+91-9876543210',
        emailAddress: 'john.doe@example.com',
        kycType: 'PASSPORT',
        kycNumber: 'A1234567',
        kycUpload: 'path/to/kyc/document.pdf',
    },

    // Consignee Information
    consignee: {
        personName: 'Jane Smith',
        address1: '456 Oak Avenue',
        city: 'Delhi',
        stateCounty: 'Delhi',
        country: 'India',
        phoneNumber: '+91-9876543211',
        emailAddress: 'jane.smith@example.com',
        kycType: 'AADHAAR',
        kycNumber: '1234-5678-9012',
        kycUpload: 'path/to/kyc/document2.pdf',
    },

    // Weight and Dimensions
    totalPieces: 2,
    weightDimensions: [
        {
            boxNo: 1,
            actualWeight: 5.5,
            length: 30,
            breadth: 20,
            height: 15,
        },
        {
            boxNo: 2,
            actualWeight: 3.2,
            length: 25,
            breadth: 15,
            height: 10,
        },
    ],
});

class AWBTestUtility implements AWBTestSuite {
    private createdAWBNumbers: string[] = [];

    async testCreateAWB(): Promise<TestResult> {
        try {
            console.log('🧪 Testing AWB Creation...');

            const testData = getSampleAWBData();
            const awb = await awbService.createAWB(testData);

            if (awb && awb.awbNumber) {
                this.createdAWBNumbers.push(awb.awbNumber);
                return {
                    success: true,
                    message: `AWB created successfully with number: ${awb.awbNumber}`,
                    data: awb,
                };
            } else {
                return {
                    success: false,
                    message: 'AWB creation failed - no AWB number returned',
                    error: 'Invalid response format',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'AWB creation failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testGetAWB(awbNumber: string): Promise<TestResult> {
        try {
            console.log(`🔍 Testing Get AWB: ${awbNumber}...`);

            const awb = await awbService.getAWB(awbNumber);

            if (awb && awb.awbNumber === awbNumber) {
                return {
                    success: true,
                    message: `AWB retrieved successfully: ${awbNumber}`,
                    data: awb,
                };
            } else {
                return {
                    success: false,
                    message: 'AWB retrieval failed',
                    error: 'AWB not found or invalid response',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'AWB retrieval failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testUpdateAWB(awbNumber: string): Promise<TestResult> {
        try {
            console.log(`✏️ Testing Update AWB: ${awbNumber}...`);

            const updateData = {
                remarks: `Updated on ${new Date().toISOString()}`,
                shipmentValue: 1200,
            };

            const updatedAWB = await awbService.updateAWB(awbNumber, updateData);

            if (updatedAWB && updatedAWB.awbNumber === awbNumber) {
                return {
                    success: true,
                    message: `AWB updated successfully: ${awbNumber}`,
                    data: updatedAWB,
                };
            } else {
                return {
                    success: false,
                    message: 'AWB update failed',
                    error: 'Update operation failed',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'AWB update failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testSearchAWBs(): Promise<TestResult> {
        try {
            console.log('🔎 Testing Search AWBs...');

            const searchResult = await awbService.searchAWBs({
                page: 1,
                limit: 10,
                sortBy: 'createdAt',
                sortOrder: 'desc',
            });

            if (searchResult && Array.isArray(searchResult.awbs)) {
                return {
                    success: true,
                    message: `Search completed. Found ${searchResult.total} AWBs`,
                    data: searchResult,
                };
            } else {
                return {
                    success: false,
                    message: 'Search failed',
                    error: 'Invalid search response format',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'Search failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testAddTrackingEvent(awbNumber: string): Promise<TestResult> {
        try {
            console.log(`📍 Testing Add Tracking Event: ${awbNumber}...`);

            const trackingEvent = {
                eventDate: new Date(),
                eventTime: new Date().toTimeString().slice(0, 5),
                eventDescription: 'Test tracking event',
                eventLocation: 'Test Location',
                eventType: 'MANUAL' as const,
                eventState: 'IN_TRANSIT',
                remarks: 'Automated test event',
            };

            const updatedAWB = await awbService.addTrackingEvent(awbNumber, trackingEvent);

            if (updatedAWB && updatedAWB.trackingEvents.length > 0) {
                return {
                    success: true,
                    message: `Tracking event added successfully to AWB: ${awbNumber}`,
                    data: updatedAWB,
                };
            } else {
                return {
                    success: false,
                    message: 'Tracking event addition failed',
                    error: 'Event not added to AWB',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'Tracking event addition failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testUpdateStatus(awbNumber: string): Promise<TestResult> {
        try {
            console.log(`🔄 Testing Update Status: ${awbNumber}...`);

            const statusUpdate = {
                newStatus: 'IN_TRANSIT' as const,
                eventDescription: 'Status updated via automated test',
                eventLocation: 'Test Hub',
                remarks: 'Automated status update test',
            };

            const updatedAWB = await awbService.updateStatus(awbNumber, statusUpdate);

            if (updatedAWB && updatedAWB.currentStatus === statusUpdate.newStatus) {
                return {
                    success: true,
                    message: `Status updated successfully for AWB: ${awbNumber}`,
                    data: updatedAWB,
                };
            } else {
                return {
                    success: false,
                    message: 'Status update failed',
                    error: 'Status not updated',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'Status update failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testAddComment(awbNumber: string): Promise<TestResult> {
        try {
            console.log(`💬 Testing Add Comment: ${awbNumber}...`);

            const comment = {
                comment: `Test comment added on ${new Date().toISOString()}`,
                attachments: [],
            };

            const updatedAWB = await awbService.addComment(awbNumber, comment);

            if (updatedAWB && updatedAWB.comments.length > 0) {
                return {
                    success: true,
                    message: `Comment added successfully to AWB: ${awbNumber}`,
                    data: updatedAWB,
                };
            } else {
                return {
                    success: false,
                    message: 'Comment addition failed',
                    error: 'Comment not added to AWB',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'Comment addition failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testGetAWBStats(): Promise<TestResult> {
        try {
            console.log('📊 Testing Get AWB Stats...');

            const stats = await awbService.getAWBStats({});

            if (stats && typeof stats.totalAWBs === 'number') {
                return {
                    success: true,
                    message: 'AWB statistics retrieved successfully',
                    data: stats,
                };
            } else {
                return {
                    success: false,
                    message: 'Stats retrieval failed',
                    error: 'Invalid stats response format',
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'Stats retrieval failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async testDeleteAWB(awbNumber: string): Promise<TestResult> {
        try {
            console.log(`🗑️ Testing Delete AWB: ${awbNumber}...`);

            await awbService.deleteAWB(awbNumber);

            // Try to get the AWB after deletion to confirm it's deleted
            try {
                await awbService.getAWB(awbNumber);
                return {
                    success: false,
                    message: 'AWB deletion failed',
                    error: 'AWB still exists after deletion',
                };
            } catch (getError) {
                // If getting the AWB fails, it means it was successfully deleted
                return {
                    success: true,
                    message: `AWB deleted successfully: ${awbNumber}`,
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'AWB deletion failed',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async seedTestData(): Promise<void> {
        console.log('🌱 Seeding AWB test data...');

        try {
            // Create multiple test AWBs
            for (let i = 0; i < 3; i++) {
                const testData = getSampleAWBData();
                testData.shipper.personName = `Test Shipper ${i + 1}`;
                testData.consignee.personName = `Test Consignee ${i + 1}`;
                testData.shipmentValue = 1000 + (i * 500);

                const result = await this.testCreateAWB();
                if (result.success) {
                    console.log(`✅ Created test AWB ${i + 1}: ${result.data?.awbNumber}`);
                } else {
                    console.log(`❌ Failed to create test AWB ${i + 1}: ${result.error}`);
                }
            }

            console.log('🌱 AWB test data seeding completed');
        } catch (error) {
            console.error('❌ Failed to seed AWB test data:', error);
        }
    }

    async clearTestData(): Promise<void> {
        console.log('🧹 Clearing AWB test data...');

        for (const awbNumber of this.createdAWBNumbers) {
            try {
                await this.testDeleteAWB(awbNumber);
                console.log(`✅ Deleted test AWB: ${awbNumber}`);
            } catch (error) {
                console.log(`❌ Failed to delete test AWB ${awbNumber}:`, error);
            }
        }

        this.createdAWBNumbers = [];
        console.log('🧹 AWB test data clearing completed');
    }

    async runFullTestSuite(): Promise<void> {
        console.log('🚀 Starting comprehensive AWB management testing...');

        const results: { test: string; result: TestResult }[] = [];

        // Test AWB creation
        const createResult = await this.testCreateAWB();
        results.push({ test: 'Create AWB', result: createResult });

        if (createResult.success && createResult.data?.awbNumber) {
            const awbNumber = createResult.data.awbNumber;

            // Test AWB retrieval
            const getResult = await this.testGetAWB(awbNumber);
            results.push({ test: 'Get AWB', result: getResult });

            // Test AWB update
            const updateResult = await this.testUpdateAWB(awbNumber);
            results.push({ test: 'Update AWB', result: updateResult });

            // Test tracking event addition
            const trackingResult = await this.testAddTrackingEvent(awbNumber);
            results.push({ test: 'Add Tracking Event', result: trackingResult });

            // Test status update
            const statusResult = await this.testUpdateStatus(awbNumber);
            results.push({ test: 'Update Status', result: statusResult });

            // Test comment addition
            const commentResult = await this.testAddComment(awbNumber);
            results.push({ test: 'Add Comment', result: commentResult });
        }

        // Test search functionality
        const searchResult = await this.testSearchAWBs();
        results.push({ test: 'Search AWBs', result: searchResult });

        // Test stats retrieval
        const statsResult = await this.testGetAWBStats();
        results.push({ test: 'Get AWB Stats', result: statsResult });

        // Print results summary
        console.log('\n📋 AWB Management Test Results:');
        console.log('=====================================');

        let passedTests = 0;
        let totalTests = results.length;

        results.forEach(({ test, result }) => {
            const status = result.success ? '✅ PASS' : '❌ FAIL';
            console.log(`${status} ${test}: ${result.message}`);
            if (result.error) {
                console.log(`   Error: ${result.error}`);
            }
            if (result.success) passedTests++;
        });

        console.log('=====================================');
        console.log(`📊 Summary: ${passedTests}/${totalTests} tests passed`);

        if (passedTests === totalTests) {
            console.log('🎉 All AWB management tests passed!');
        } else {
            console.log(`⚠️ ${totalTests - passedTests} test(s) failed. Please check the errors above.`);
        }
    }
}

// Create and export the test utility instance
const awbTestUtility = new AWBTestUtility();

// Make it available globally for browser console testing
if (typeof window !== 'undefined') {
    (window as any).testAWBManagement = awbTestUtility.runFullTestSuite.bind(awbTestUtility);
    (window as any).seedAWBData = awbTestUtility.seedTestData.bind(awbTestUtility);
    (window as any).clearAWBData = awbTestUtility.clearTestData.bind(awbTestUtility);
    (window as any).awbTestUtility = awbTestUtility;
}

export default awbTestUtility; 